import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, CheckCircle, XCircle, Settings, Download } from 'lucide-react';
import './AdminVendors.css';

const AdminProducts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [vendors, setVendors] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', unit: 'piece', weight: '', hsn_code: '', gst_slab: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: ''
  });

  // simple toast notifications
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const adminHeaders = () => {
    const adminToken = window.localStorage.getItem('adminToken');
    const vendorToken = window.localStorage.getItem('token');
    const legacyKey = window.localStorage.getItem('ADMIN_API_KEY') || window.localStorage.getItem('adminApiKey');
    const headers = {};
    if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`;
    else if (vendorToken) headers['Authorization'] = `Bearer ${vendorToken}`;
    if (legacyKey) headers['x-admin-key'] = legacyKey;
    return headers;
  };

  // Download image function
  const downloadImage = async (imageUrl, productName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from URL or use product name
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || `${productName.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Image downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading image:', error);
      showToast('Failed to download image', 'error');
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch('/api/admin/vendors', { headers: adminHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVendors(data.vendors || data.items || []);
      }
    } catch (_) {}
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams();
      const trimmedSearch = (searchQuery || '').trim();
      if (/^PRDT\d{3,}$/i.test(trimmedSearch)) {
        qs.set('product_id', trimmedSearch.toUpperCase());
      } else if (searchQuery) {
        qs.set('search', searchQuery);
      }
      if (categoryFilter && categoryFilter !== 'all') qs.set('category', categoryFilter);
      if (statusFilter && statusFilter !== 'all') qs.set('status', statusFilter);
      // If the search text looks like a vendor code VDR###, filter by vendor_code
      if (/^VDR\d{3,}$/i.test(trimmedSearch)) {
        qs.set('vendor_code', trimmedSearch.toUpperCase());
      } else if (/^\d+$/.test(trimmedSearch)) {
        // numeric vendor internal id
        qs.set('vendor_id', trimmedSearch);
      } else if (vendorFilter && vendorFilter !== 'all') {
        qs.set('vendor_id', vendorFilter);
      }
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      qs.set('page', String(page));
      qs.set('limit', String(limit));
      const res = await fetch(`/api/admin/products?${qs.toString()}`, { headers: adminHeaders() });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load products');
      }
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [searchQuery, categoryFilter, statusFilter, vendorFilter, dateFrom, dateTo, page, limit]);

  const openAdd = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', sku: '', category: '', price: '', mrp: '', cost_price: '', image_url: '', unit: 'piece', weight: '', hsn_code: '', gst_slab: '', stock_on_hand: 0, min_stock_level: 0, reorder_point: 0, reorder_quantity: 0, vendor_id: '' });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setFormData({
      name: p.name || '', description: p.description || '', sku: p.sku || '', category: p.category || '', price: p.price || '', mrp: p.mrp || '', cost_price: p.cost_price || '', image_url: p.image_url || '', unit: p.unit || 'piece', weight: p.weight || '', hsn_code: p.hsn_code || '', gst_slab: p.gst_slab || '', stock_on_hand: p.stock_on_hand || 0, min_stock_level: p.min_stock_level || 0, reorder_point: p.reorder_point || 0, reorder_quantity: p.reorder_quantity || 0, vendor_id: p.vendor_id || ''
    });
    setShowModal(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const isAdmin = true;
      if (isAdmin && !formData.vendor_id) {
        showToast('Please select a vendor', 'error');
        return;
      }
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct ? `/api/admin/products/${editingProduct.id}` : '/api/admin/products';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify(formData) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to save product');
      }
      setShowModal(false);
      setEditingProduct(null);
      fetchProducts();
      showToast(editingProduct ? 'Product updated' : 'Product created', 'success');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`/api/admin/products/${p.id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ status: next }) });
      if (!res.ok) throw new Error('Failed to update status');
      fetchProducts();
      showToast(`Product ${next === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this product permanently?')) return;
    try {
      const res = await fetch(`/api/admin/products/${id}`, { method: 'DELETE', headers: adminHeaders() });
      if (!res.ok) throw new Error('Failed to delete product');
      fetchProducts();
      showToast('Product deleted', 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const runBulk = async () => {
    if (selectedProducts.length === 0 || !bulkAction) return;
    try {
      if (bulkAction === 'delete') {
        if (!window.confirm(`Permanently delete ${selectedProducts.length} products?`)) return;
        const res = await fetch('/api/admin/products/bulk-delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ productIds: selectedProducts }) });
        if (!res.ok) throw new Error('Bulk delete failed');
      } else {
        const res = await fetch('/api/admin/products/bulk-status', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...adminHeaders() }, body: JSON.stringify({ productIds: selectedProducts, status: bulkAction }) });
        if (!res.ok) throw new Error('Bulk status failed');
      }
      setSelectedProducts([]);
      setBulkAction('');
      setShowBulkActions(false);
      fetchProducts();
      showToast('Bulk action completed', 'success');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleSelection = (id) => {
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedProducts(products.map(p => p.id));
  const clearSel = () => setSelectedProducts([]);

  return (
    <div className="admin-vendors">
      <div className="admin-vendors-header">
        <div className="admin-vendors-header-content">
          <h1>Product Management</h1>
          <p>Manage all products across vendors</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={20} />
          Add Product
        </button>
      </div>

      <div className="admin-vendors-filters">
        <div className="admin-filters-left" style={{ flexWrap: 'wrap' }}>
          <div className="admin-search-container">
            <Search size={20} className="admin-search-icon" />
            <input type="text" placeholder="Search by name, SKU, Product ID (e.g. PRDT025), vendor or vendor ID" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }} className="admin-search-input" />
          </div>
          <div className="admin-filter-group">
            <Filter size={20} />
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} className="admin-filter-select">
              <option value="all">All Categories</option>
              <option value="Food & Beverages">FAB001 - Food & Beverages</option>
              <option value="Fresh Produce">FPD001 - Fresh Produce</option>
              <option value="Dairy">DARY001 - Dairy</option>
              <option value="Bakery">BKRY001 - Bakery</option>
              <option value="Pantry">PTRY001 - Pantry</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="admin-filter-input" />
            <span style={{ margin: '0 6px' }}>to</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="admin-filter-input" />
          </div>
          <div className="admin-filter-group">
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="admin-filter-select" style={{ minWidth: 140 }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <select 
              value={vendorFilter} 
              onChange={(e) => { setVendorFilter(e.target.value); setPage(1); }} 
              className="admin-filter-select"
              style={{ minWidth: 220 }}
            >
              <option value="all">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.shop_name || v.owner_name} ({v.email})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="admin-filters-right">
          <div className="admin-results-count">{products.length} of {total} products</div>
          {selectedProducts.length > 0 && (
            <div className="admin-bulk-actions">
              <span className="admin-selected-count">{selectedProducts.length} selected</span>
              <button className="btn btn-secondary" onClick={() => setShowBulkActions(!showBulkActions)}>
                <Settings size={16} />
                Bulk Actions
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toasts shown above the product table */}
      {!!toasts.length && (
        <div style={{ display: 'grid', gap: 8, margin: '12px 0' }}>
          {toasts.map(t => (
            <div key={t.id} style={{ padding: '10px 14px', borderRadius: 8, color: '#0b1f0b', background: t.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(220,38,38,0.15)', border: `1px solid ${t.type === 'success' ? '#10b981' : '#dc2626'}` }}>
              {t.message}
            </div>
          ))}
        </div>
      )}

      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}

      {showBulkActions && selectedProducts.length > 0 && (
        <div className="admin-bulk-actions-panel">
          <div className="admin-bulk-actions-content">
            <h4>Bulk Actions ({selectedProducts.length} products selected)</h4>
            <div className="admin-bulk-controls">
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="admin-filter-select">
                <option value="">Select Action</option>
                <option value="active">Set Active</option>
                <option value="inactive">Set Inactive</option>
                <option value="discontinued">Set Discontinued</option>
                <option value="delete">Delete</option>
              </select>
              <button className="btn btn-primary" onClick={runBulk} disabled={!bulkAction}>Apply</button>
              <button className="btn btn-secondary" onClick={clearSel}>Clear</button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-vendors-table-container">
        <table className="admin-vendors-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selectedProducts.length === products.length && products.length > 0} onChange={(e) => e.target.checked ? selectAll() : clearSel()} /></th>
              <th>Product</th>
              <th>Product ID</th>
              <th>Vendor ID</th>
              <th>Vendor</th>
              <th>Category</th>
              <th>Price</th>
              <th>On Hand</th>
              <th>Reserved</th>
              <th>Available</th>
              <th>Images</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && products.map(p => (
              <tr key={p.id}>
                <td><input type="checkbox" checked={selectedProducts.includes(p.id)} onChange={() => toggleSelection(p.id)} /></td>
                <td>
                  <div className="admin-vendor-info">
                    <div className="admin-vendor-avatar">{(p.name || 'P').charAt(0)}</div>
                    <div>
                      <div className="admin-vendor-name">{p.name}</div>
                      <div className="admin-vendor-location">SKU: {p.sku}</div>
                    </div>
                  </div>
                </td>
                <td><span style={{ fontWeight: 'bold', color: '#2563eb' }}>{p.product_id || 'N/A'}</span></td>
                <td><span style={{ color: '#065f46', fontWeight: 600 }}>{p.vendor_code || p.vendor_id || 'N/A'}</span></td>
                <td>{p.vendor_name || p.owner_name || '-'}</td>
                <td>{p.category}</td>
                <td>₹{Number(p.price).toFixed(2)}</td>
                <td>{p.stock_on_hand ?? 0}</td>
                <td>{p.stock_reserved ?? 0}</td>
                <td>{p.stock_available ?? 0}</td>
                <td>
                  <div className="product-images-display">
                    {p.product_images && Array.isArray(p.product_images) && p.product_images.length > 0 ? (
                      <div className="images-container">
                        <img 
                          src={p.product_images[0]} 
                          alt={p.name}
                          className="product-image-thumbnail"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="no-image-fallback" 
                          style={{ display: 'none' }}
                        >
                          <span>No Image</span>
                        </div>
                        <div className="image-download-overlay">
                          <button 
                            className="download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadImage(p.product_images[0], p.name);
                            }}
                            title="Download Image"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                        {p.product_images.length > 1 && (
                          <div className="additional-images-count">
                            +{p.product_images.length - 1}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`admin-status-badge ${p.status === 'active' ? 'status-active' : p.status === 'inactive' ? 'status-suspended' : 'status-blocked'}`}>{p.status}</span>
                </td>
                <td>{p.created_at ? new Date(p.created_at).toLocaleString() : '-'}</td>
                <td>
                  <div className="admin-vendor-actions">
                    <button className="admin-action-btn" title="Edit" onClick={() => openEdit(p)}><Edit2 size={16} /></button>
                    <button className="admin-action-btn" title={p.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatus(p)}>
                      {p.status === 'active' ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                    </button>
                    <button className="admin-action-btn block" title="Delete" onClick={() => deleteOne(p.id)}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {loading && (
              <tr><td colSpan={14} style={{ padding: 24, textAlign: 'center' }}>Loading...</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr><td colSpan={14} style={{ padding: 24, textAlign: 'center' }}>No products found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>Rows:</span>
          <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="pagination">
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="page-info">Page {page} of {Math.max(1, Math.ceil(total / limit))}</span>
          <button className="btn btn-secondary" disabled={page >= Math.ceil(total / limit)} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={saveProduct} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>SKU *</label>
                  <input type="text" name="sku" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vendor *</label>
                  <select name="vendor_id" value={formData.vendor_id} onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })} required>
                    <option value="">Select Vendor</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.shop_name || v.owner_name} ({v.email})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select name="category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required>
                    <option value="">Select Category</option>
                    <option value="Food & Beverages">FAB001 - Food & Beverages</option>
                    <option value="Fresh Produce">FPD001 - Fresh Produce</option>
                    <option value="Dairy">DARY001 - Dairy</option>
                    <option value="Bakery">BKRY001 - Bakery</option>
                    <option value="Pantry">PTRY001 - Pantry</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cost Price *</label>
                  <input type="number" step="0.01" name="cost_price" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Platform Price</label>
                  <input type="number" step="0.01" name="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>MRP</label>
                  <input type="number" step="0.01" name="mrp" value={formData.mrp} onChange={(e) => setFormData({ ...formData, mrp: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>GST Slab (%)</label>
                  <input type="number" step="1" min="0" max="28" name="gst_slab" value={formData.gst_slab} onChange={(e) => setFormData({ ...formData, gst_slab: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
                <div className="form-group">
                  <label>HSN Code</label>
                  <input type="text" name="hsn_code" value={formData.hsn_code} onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea name="description" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unit</label>
                  <select name="unit" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
                    <option value="piece">Piece</option>
                    <option value="kg">Kilogram</option>
                    <option value="g">Gram</option>
                    <option value="l">Liter</option>
                    <option value="ml">Milliliter</option>
                    <option value="box">Box</option>
                    <option value="pack">Pack</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input type="number" step="0.01" name="weight" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image URL</label>
                  <input type="url" name="image_url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
                </div>
              </div>

              <div className="inventory-section">
                <h3>Inventory Settings</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Stock On Hand</label>
                    <input type="number" name="stock_on_hand" value={formData.stock_on_hand} onChange={(e) => setFormData({ ...formData, stock_on_hand: e.target.value })} min="0" />
                  </div>
                  <div className="form-group">
                    <label>Min Stock Level</label>
                    <input type="number" name="min_stock_level" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Reorder Point</label>
                    <input type="number" name="reorder_point" value={formData.reorder_point} onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })} min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Reorder Quantity</label>
                  <input type="number" name="reorder_quantity" value={formData.reorder_quantity} onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })} min="0" />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingProduct ? 'Update Product' : 'Add Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;


